import { useState, useMemo } from 'react'
import { Order } from '../types'

interface OrdersTableProps {
  orders: Order[]
  allOrders: Order[]
  onSelectOrder: (order: Order) => void
}

function formatDate(dateStr: string): string {
  if (!dateStr || dateStr.length !== 8) return dateStr || '-'
  return (
    dateStr.substring(6, 8) +
    '/' +
    dateStr.substring(4, 6) +
    '/' +
    dateStr.substring(0, 4)
  )
}

function parseDateStr(dateStr: string): number {
  if (!dateStr || dateStr.length !== 8) return 0
  return parseInt(dateStr, 10)
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)
}

type Tab = 'attributed' | 'influenced' | 'others'
type SortField = 'date' | 'purchaseRevenue' | 'items'
type SortDir = 'asc' | 'desc'
interface Filters {
  dateFrom: string
  dateTo: string
  priceMin: string
  priceMax: string
  itemsMin: string
  itemsMax: string
}

const emptyFilters: Filters = {
  dateFrom: '',
  dateTo: '',
  priceMin: '',
  priceMax: '',
  itemsMin: '',
  itemsMax: '',
}

const PAGE_SIZE = 30

function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField | null; sortDir: SortDir }) {
  const isActive = sortField === field
  return (
    <span className={`sort-icon ${isActive ? 'active' : ''}`}>
      {isActive ? (sortDir === 'asc' ? '\u2191' : '\u2193') : '\u2195'}
    </span>
  )
}

export function OrdersTable({ orders, allOrders, onSelectOrder }: OrdersTableProps) {
  const [activeTab, setActiveTab] = useState<Tab>('attributed')
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<Filters>(emptyFilters)
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  // 3 abas em PARTIÇÃO (sem dupla contagem entre elas):
  //  - Atribuídos: match de nome de produto (base do ROI)
  //  - Influenciados: viu vídeo e comprou depois, SEM estar em Atribuídos
  //  - Outros: todo o resto (sem widdeUid ou sem nenhuma das duas flags)
  const attributedOrders = orders.filter(o => o.widdeInfluenciou)
  const influencedOrders = orders.filter(o => o.viewInfluenced && !o.widdeInfluenciou)
  const othersOrders = allOrders.filter(o => {
    const isWiddeValid =
      o.widdeUid && o.widdeUid !== '(not set)' &&
      o.transactionId && !/^\(.*\)$/.test(o.transactionId)
    return !isWiddeValid || (!o.widdeInfluenciou && !o.viewInfluenced)
  })

  const baseOrders =
    activeTab === 'attributed' ? attributedOrders :
    activeTab === 'influenced' ? influencedOrders :
    othersOrders

  // Apply filters
  const filteredOrders = useMemo(() => {
    let result = baseOrders

    if (filters.dateFrom) {
      const from = filters.dateFrom.replace(/-/g, '')
      result = result.filter(o => o.date >= from)
    }
    if (filters.dateTo) {
      const to = filters.dateTo.replace(/-/g, '')
      result = result.filter(o => o.date <= to)
    }
    if (filters.priceMin) {
      const min = parseFloat(filters.priceMin)
      if (!isNaN(min)) result = result.filter(o => o.purchaseRevenue >= min)
    }
    if (filters.priceMax) {
      const max = parseFloat(filters.priceMax)
      if (!isNaN(max)) result = result.filter(o => o.purchaseRevenue <= max)
    }
    if (filters.itemsMin) {
      const min = parseInt(filters.itemsMin, 10)
      if (!isNaN(min)) result = result.filter(o => o.items.length >= min)
    }
    if (filters.itemsMax) {
      const max = parseInt(filters.itemsMax, 10)
      if (!isNaN(max)) result = result.filter(o => o.items.length <= max)
    }
    return result
  }, [baseOrders, filters])

  // Apply sorting
  const sortedOrders = useMemo(() => {
    if (!sortField) return filteredOrders

    const sorted = [...filteredOrders]
    sorted.sort((a, b) => {
      let cmp = 0
      switch (sortField) {
        case 'date':
          cmp = parseDateStr(a.date) - parseDateStr(b.date)
          break
        case 'purchaseRevenue':
          cmp = (a.purchaseRevenue || 0) - (b.purchaseRevenue || 0)
          break
        case 'items':
          cmp = a.items.length - b.items.length
          break
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
    return sorted
  }, [filteredOrders, sortField, sortDir])

  const visibleOrders = sortedOrders.slice(0, visibleCount)
  const hasMore = visibleCount < sortedOrders.length

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab)
    setVisibleCount(PAGE_SIZE)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDir === 'desc') {
        setSortDir('asc')
      } else {
        // Third click: remove sort
        setSortField(null)
        setSortDir('desc')
      }
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const updateFilter = (key: keyof Filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setVisibleCount(PAGE_SIZE)
  }

  const hasActiveFilters = Object.values(filters).some(val => val !== '')

  const clearFilters = () => {
    setFilters(emptyFilters)
    setVisibleCount(PAGE_SIZE)
  }

  if (allOrders.length === 0) {
    return null
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb', marginBottom: 16 }}>
        <button
          onClick={() => handleTabChange('attributed')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: 'none',
            fontSize: 14,
            fontWeight: 500,
            color: activeTab === 'attributed' ? '#6366f1' : '#6b7280',
            cursor: 'pointer',
            borderBottom: `2px solid ${activeTab === 'attributed' ? '#6366f1' : 'transparent'}`,
            marginBottom: -2,
          }}
          title="Viu o produto num vídeo e comprou o mesmo produto (match de nome) — base do ROI"
        >
          Atribuídos à Widde
          <span style={{ marginLeft: 6, fontSize: 12, color: activeTab === 'attributed' ? '#6366f1' : '#9ca3af' }}>
            ({attributedOrders.length})
          </span>
        </button>
        <button
          onClick={() => handleTabChange('influenced')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: 'none',
            fontSize: 14,
            fontWeight: 500,
            color: activeTab === 'influenced' ? '#6366f1' : '#6b7280',
            cursor: 'pointer',
            borderBottom: `2px solid ${activeTab === 'influenced' ? '#6366f1' : 'transparent'}`,
            marginBottom: -2,
          }}
          title="Viu um vídeo e comprou depois (30d), independente do produto — sem contar os já atribuídos"
        >
          Influenciados pela Widde
          <span style={{ marginLeft: 6, fontSize: 12, color: activeTab === 'influenced' ? '#6366f1' : '#9ca3af' }}>
            ({influencedOrders.length})
          </span>
        </button>
        <button
          onClick={() => handleTabChange('others')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: 'none',
            fontSize: 14,
            fontWeight: 500,
            color: activeTab === 'others' ? '#6366f1' : '#6b7280',
            cursor: 'pointer',
            borderBottom: `2px solid ${activeTab === 'others' ? '#6366f1' : 'transparent'}`,
            marginBottom: -2,
          }}
        >
          Outros Pedidos
          <span style={{ marginLeft: 6, fontSize: 12, color: activeTab === 'others' ? '#6366f1' : '#9ca3af' }}>
            ({othersOrders.length})
          </span>
        </button>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, paddingRight: 4 }}>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="filter-clear-btn">
              Limpar filtros
            </button>
          )}
          <button
            onClick={() => setShowFilters(prev => !prev)}
            className={`filter-toggle-btn ${showFilters || hasActiveFilters ? 'active' : ''}`}
          >
            <span style={{ fontSize: 14 }}>{'\u2630'}</span>
            {' '}Filtros
            {hasActiveFilters && (
              <span className="filter-badge">
                {Object.values(filters).filter(v => v !== '').length}
              </span>
            )}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="filters-bar">
          <div className="filter-group">
            <label>Data</label>
            <div className="filter-row">
              <input
                type="date"
                value={filters.dateFrom}
                onChange={e => updateFilter('dateFrom', e.target.value)}
                placeholder="De"
              />
              <span className="filter-separator">a</span>
              <input
                type="date"
                value={filters.dateTo}
                onChange={e => updateFilter('dateTo', e.target.value)}
                placeholder="Ate"
              />
            </div>
          </div>
          <div className="filter-group">
            <label>Receita (R$)</label>
            <div className="filter-row">
              <input
                type="number"
                value={filters.priceMin}
                onChange={e => updateFilter('priceMin', e.target.value)}
                placeholder="Min"
                min="0"
                step="0.01"
              />
              <span className="filter-separator">a</span>
              <input
                type="number"
                value={filters.priceMax}
                onChange={e => updateFilter('priceMax', e.target.value)}
                placeholder="Max"
                min="0"
                step="0.01"
              />
            </div>
          </div>
          <div className="filter-group">
            <label>Itens</label>
            <div className="filter-row">
              <input
                type="number"
                value={filters.itemsMin}
                onChange={e => updateFilter('itemsMin', e.target.value)}
                placeholder="Min"
                min="0"
                step="1"
              />
              <span className="filter-separator">a</span>
              <input
                type="number"
                value={filters.itemsMax}
                onChange={e => updateFilter('itemsMax', e.target.value)}
                placeholder="Max"
                min="0"
                step="1"
              />
            </div>
          </div>
        </div>
      )}

      {hasActiveFilters && (
        <div className="filter-results-info">
          Mostrando {sortedOrders.length} de {baseOrders.length} pedidos
        </div>
      )}

      {sortedOrders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af', fontSize: 14 }}>
          {hasActiveFilters ? 'Nenhum pedido encontrado com os filtros aplicados' : 'Nenhum pedido nesta categoria'}
        </div>
      ) : (
        <>
        <table className="orders-table">
          <thead>
            <tr>
              <th>Transaction ID</th>
              <th className="sortable-th" onClick={() => handleSort('date')}>
                Data <SortIcon field="date" sortField={sortField} sortDir={sortDir} />
              </th>
              {activeTab !== 'others' && <th>Widde UID</th>}
              <th className="sortable-th" onClick={() => handleSort('purchaseRevenue')}>
                Receita <SortIcon field="purchaseRevenue" sortField={sortField} sortDir={sortDir} />
              </th>
              <th className="sortable-th" onClick={() => handleSort('items')}>
                Itens <SortIcon field="items" sortField={sortField} sortDir={sortDir} />
              </th>
              <th>Status</th>
              {activeTab !== 'others' && <th>Widde</th>}
            </tr>
          </thead>
          <tbody>
            {visibleOrders.map(order => {
              const hasWidde = order.widdeUid && order.widdeUid !== '(not set)'

              return (
                <tr
                  key={order.transactionId}
                  onClick={() => onSelectOrder(order)}
                >
                  <td>
                    <code>{order.transactionId}</code>
                  </td>
                  <td>{formatDate(order.date)}</td>
                  {activeTab !== 'others' && (
                    <td>
                      {hasWidde ? (
                        <span className="badge badge-widde">{order.widdeUid}</span>
                      ) : (
                        <span className="badge badge-no-widde">sem widde</span>
                      )}
                    </td>
                  )}
                  <td className="revenue">
                    {formatCurrency(order.purchaseRevenue)}
                  </td>
                  <td>{order.items.length}</td>
                  <td>
                    {order.processed ? (
                      <span
                        className="badge"
                        style={{ background: '#dbeafe', color: '#1e40af' }}
                      >
                        Processado
                      </span>
                    ) : (
                      <span
                        className="badge"
                        style={{ background: '#f3f4f6', color: '#6b7280' }}
                      >
                        Pendente
                      </span>
                    )}
                  </td>
                  {activeTab !== 'others' && (
                    <td>
                      {/* Atribuído = match de nome de produto (base do ROI).
                          Influenciado = viu vídeo e comprou depois (30d),
                          independente de produto. Um pedido pode ser os dois. */}
                      {order.widdeInfluenciou || order.viewInfluenced ? (
                        <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                          {order.widdeInfluenciou && (
                            <span
                              className="badge"
                              style={{ background: '#dcfce7', color: '#166534' }}
                            >
                              Atribuído
                            </span>
                          )}
                          {order.viewInfluenced && (
                            <span
                              className="badge"
                              style={{ background: '#ede9fe', color: '#5b21b6' }}
                            >
                              Influenciado
                            </span>
                          )}
                        </span>
                      ) : (
                        <span style={{ color: '#d1d5db', fontSize: 12 }}>
                          {'\u2014'}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
        {hasMore && (
          <div style={{ textAlign: 'center', paddingTop: 16 }}>
            <button
              onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
              style={{
                padding: '8px 24px',
                border: '1px solid #e5e7eb',
                background: '#f9fafb',
                borderRadius: 6,
                fontSize: 13,
                color: '#374151',
                cursor: 'pointer',
              }}
            >
              Mostrar mais ({sortedOrders.length - visibleCount} restantes)
            </button>
          </div>
        )}
        </>
      )}
    </div>
  )
}
